import React, { useMemo } from "react";
import { Btn, Card, NoticeState } from "../../../ForBrands/ui/BrandUI";
import { CollapseAllButton, useCollapseAll } from "../../../ForBrands/ui/CollapsibleCard";
import { MobileCard } from "../../../ForBrands/ui/mobile/MobileUI";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { StaticChip } from "../mobile/mobileProfileSections";
import ConsistencyCard from "./ConsistencyCard";
import GameByGameCard from "./GameByGameCard";
import HistoricalCompsCard from "./HistoricalCompsCard";
import PeersTeammatesCard from "./PeersTeammatesCard";
import PlayerArchetypeCard from "./PlayerArchetypeCard";
import RadarFallbackCard from "./RadarFallbackCard";
import ScoringMixCard from "./ScoringMixCard";
import SeasonBoxScoreCard from "./SeasonBoxScoreCard";
import SkillProfileCard from "./SkillProfileCard";
import { RETRY_REASONS, buildPerformanceView, loadingLabel, reasonNote, voiceFor } from "./athletePerformanceModel";
import { KeepFocus } from "./perfPieces";

// The athlete profile's Performance section with Jayson's cards (spec B3),
// on desktop (the Performance tab under the rate card) and on phones (the
// Performance tab of ProfileTabsMobile), the cards choosing their phone
// look themselves:
//   the sport and, on desktop, one Collapse all / Expand all control (B9);
//   (a) the season box score with where its stats come from;
//   (b) Player Archetype; its shell while the season's peer group is being
//       built (the hook asks again until it is ready, D4 b and c); for a
//       season of games the team entered, a note that the archetype and
//       the comps need a league season (D8); else the profile's radar with
//       why no archetype can come;
//   (c) Skill Profile, (d) Scoring Mix, (e) Game by Game;
//   (f) Consistency | Historical Comps (two columns on desktop; Comps only
//       with a comparison pool);
//   (g) Peers and teammates.
// Every card is collapsible and open; nothing is behind a paywall (B10).
//
// `perf` is useAthletePerformance(): while it loads, the box score shows
// with a loading card under it; when the section is unavailable (no league
// record, a feed outage, a build that never finished) the page's own
// performance cards (`fallback`) show instead, under a note when the
// reason is worth saying, with Try again when a later request may work
// (the coach team page's unavailable state, in a notice here because the
// profile's own cards still show). The cards read the default season's
// answer (perf.baseEnvelope); only Game by Game follows a season picked
// in it (perf.envelope), as on the mock.

const cardId = (name) => `athlete-perf-${name}`;

const AthletePerformance = ({ perf, model, narratives, fallback = null }) => {
  const phone = usePhoneTheme();
  const self = Boolean(narratives?.self);
  const status = perf?.status || "loading";
  const envelope = status === "ready" ? perf.envelope : null;
  const base = status === "ready" ? perf.baseEnvelope || envelope : null;
  const view = useMemo(
    () => (base ? buildPerformanceView({ envelope: base, gamesEnvelope: envelope, model, selfView: self }) : null),
    [base, envelope, model, self],
  );
  const ids = [cardId("box")];
  if (view) {
    if (view.archetype || view.archetypeShell) ids.push(cardId("archetype"));
    else if (!view.archetypeNote) ids.push(cardId("radar"));
    if (view.skills) ids.push(cardId("skills"));
    if (view.scoring) ids.push(cardId("mix"));
    ids.push(cardId("games"));
    if (view.consistency) ids.push(cardId("consistency"));
    if (view.comps) ids.push(cardId("comps"));
    if (view.peers) ids.push(cardId("peers"));
  }
  const collapse = useCollapseAll(ids);
  const voice = view?.voice || voiceFor({ selfView: self, firstName: model?.firstName });

  if (status === "unavailable") {
    const note = reasonNote(perf?.reason, voice);
    const retry = RETRY_REASONS.has(perf?.reason) && typeof perf?.retry === "function" ? perf.retry : null;
    return (
      <>
        {note && (
          <NoticeState role="status" className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
            <span className="min-w-0 flex-1 basis-[220px]">{note}</span>
            {retry && (
              <Btn variant="secondary" size="sm" onClick={retry}>
                Try again
              </Btn>
            )}
          </NoticeState>
        )}
        {fallback}
      </>
    );
  }

  const sport = model?.sport && narratives?.showStats ? model.sport : null;
  const head = phone ? (
    sport && <StaticChip>{sport}</StaticChip>
  ) : (
    <div className="flex flex-wrap items-center justify-between gap-3 py-[6px]">
      {sport ? <span className="inline-flex h-12 items-center rounded-full bg-bm-ink px-6 bm-label-l text-white">{sport}</span> : <span />}
      {view && <CollapseAllButton control={collapse} />}
    </div>
  );
  // One card from the loading state to the cards (keyed: the notices come
  // before it once the answer is in), so a School | Grassroots pick made
  // while the section loads or builds, and the focus on it, stay (D7).
  const box = (
    <SeasonBoxScoreCard
      key="box"
      model={model}
      narratives={narratives}
      showSummary={Boolean(view) && !view.hasReads}
      cardProps={collapse.cardProps(cardId("box"))}
    />
  );
  const Frame = phone ? React.Fragment : "div";
  const frameProps = phone ? {} : { className: "flex flex-col gap-4" };

  if (!view) {
    const label = loadingLabel(status, voice);
    return (
      <Frame {...frameProps}>
        {head}
        {box}
        {phone ? (
          <MobileCard>
            <p role="status" className="py-6 text-center bm-body-m text-bm-muted">
              {label}
            </p>
          </MobileCard>
        ) : (
          <Card>
            <p role="status" className="py-10 text-center bm-body-m text-bm-muted">
              {label}
            </p>
          </Card>
        )}
      </Frame>
    );
  }

  const approximate = view.peer.approximate;
  const consistency = view.consistency && <ConsistencyCard view={view.consistency} cardProps={collapse.cardProps(cardId("consistency"))} />;
  const comps = view.comps && <HistoricalCompsCard view={view.comps} cardProps={collapse.cardProps(cardId("comps"))} />;
  // The card and its shell are one card on screen (PlayerArchetypeCard): the
  // ranks arriving fill the shell in where it stands. When a re-ask settles
  // with no group instead, the radar takes the shell's place and KeepFocus
  // moves a focus the shell held to the radar card's toggle.
  let archetype;
  let slot = "radar";
  if (view.archetype || view.archetypeShell) {
    slot = "archetype";
    archetype = (
      <PlayerArchetypeCard
        view={view.archetype}
        shell={view.archetypeShell}
        approximate={approximate}
        cardProps={collapse.cardProps(cardId("archetype"))}
      />
    );
  } else if (view.archetypeNote) {
    slot = "note";
    archetype = <NoticeState>{view.archetypeNote}</NoticeState>;
  } else {
    archetype = <RadarFallbackCard groups={model?.radarGroups || []} note={view.radarNote} cardProps={collapse.cardProps(cardId("radar"))} />;
  }
  return (
    <Frame {...frameProps}>
      {head}
      {view.notices.map((note) => (
        <NoticeState key={note} role="status">
          {note}
        </NoticeState>
      ))}
      {box}
      <KeepFocus swap={slot}>{archetype}</KeepFocus>
      {view.skills && <SkillProfileCard view={view.skills} approximate={approximate} cardProps={collapse.cardProps(cardId("skills"))} />}
      {view.scoring && <ScoringMixCard view={view.scoring} approximate={approximate} cardProps={collapse.cardProps(cardId("mix"))} />}
      <GameByGameCard
        view={view.games}
        voice={view.voice}
        seasonKey={perf.seasonKey}
        pending={perf.pending}
        notice={perf.notice}
        onSeasonChange={perf.select}
        cardProps={collapse.cardProps(cardId("games"))}
      />
      {phone ? (
        <>
          {consistency}
          {comps}
        </>
      ) : (
        (consistency || comps) && <div className={consistency && comps ? "grid items-start gap-4 lg:grid-cols-2" : "flex flex-col gap-4"}>{consistency}{comps}</div>
      )}
      {view.peers && <PeersTeammatesCard view={view.peers} cardProps={collapse.cardProps(cardId("peers"))} />}
    </Frame>
  );
};

export default AthletePerformance;
